import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { getSupabaseAnonKey, getSupabaseBaseUrl } from "@/services/supabaseClient";

async function handoff(body, encounterSession = "") {
  const base = getSupabaseBaseUrl();
  const anon = getSupabaseAnonKey();
  const response = await fetch(`${String(base || "").replace(/\/$/, "")}/functions/v1/adult-dependant-browser-handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: anon, ...(encounterSession ? { "x-encounter-session": encounterSession } : {}) },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.message || payload?.error || "The private consultation could not be opened.");
  return payload;
}

export default function AdultDependantBrowserJoin() {
  const { invitationId } = useParams();
  const location = useLocation();
  const linkToken = React.useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [recipientEmail, setRecipientEmail] = React.useState("");
  const [verificationCode, setVerificationCode] = React.useState("");
  const [state, setState] = React.useState("start");
  const [session, setSession] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const requestCode = async () => {
    setBusy(true); setMessage("");
    try {
      await handoff({ action: "request_code", invitationId, linkToken, recipientEmail });
      setState("verify"); setMessage("A verification code was sent to the invited contact.");
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setMessage("");
    try {
      const result = await handoff({ action: "verify", invitationId, linkToken, recipientEmail, verificationCode });
      setSession(result.encounterSession); setState("ready"); setMessage("Contact verified. This session can only join this appointment.");
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };
  const join = async () => {
    setBusy(true); setMessage("");
    try { await handoff({ action: "join" }, session); setState("joined"); setMessage("You are checked in. Your clinician will confirm identity, attendance and treatment consent before clinical work begins."); }
    catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };

  return (
    <main className="auth-bg min-h-[100dvh] px-4 py-10 text-on-surface">
      <section className="mx-auto max-w-lg rounded-3xl bg-white p-6 shadow-sm md:p-8">
        <p className="text-label-medium text-primary">Private appointment access</p>
        <h1 className="mt-2 text-title-large">Join your consultation</h1>
        <p className="mt-3 text-body-medium text-on-surface-var">This link is for one appointment only. It does not give access to myEHR, prescriptions or any other health record.</p>
        {state === "start" && <><label className="mt-6 block text-label-medium" htmlFor="recipient-email">Invited email address</label><input id="recipient-email" type="email" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} className="mt-2 w-full rounded-xl border border-outline p-3" autoComplete="email" /><button type="button" onClick={requestCode} disabled={busy || !linkToken} className="mt-5 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? "Sending…" : "Send verification code"}</button></>}
        {state === "verify" && <><label className="mt-6 block text-label-medium" htmlFor="verification-code">Verification code</label><input id="verification-code" inputMode="numeric" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} className="mt-2 w-full rounded-xl border border-outline p-3" autoComplete="one-time-code" /><button type="button" onClick={verify} disabled={busy || verificationCode.length < 6} className="mt-5 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? "Verifying…" : "Verify and continue"}</button></>}
        {state === "ready" && <button type="button" onClick={join} disabled={busy} className="mt-6 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? "Joining…" : "Join appointment"}</button>}
        {state === "joined" && <div className="mt-6 rounded-xl bg-primary-container p-4 text-body-medium">You are in the restricted appointment waiting room. Clinical work cannot begin until your clinician records the required encounter decisions.</div>}
        {message && <p role="status" className="mt-4 text-body-medium text-on-surface-var">{message}</p>}
      </section>
    </main>
  );
}
